<template>
  <div class="form-field-container">
    <label :for="id" class="form-label">{{ label }}</label>
    <div class="required-mark-container">
      <span v-if="required" class="required-mark">必須</span>
    </div>
    <div class="upload-wrapper-container">
      <div class="image-upload-wrapper">
        <!-- 画像プレビューエリア -->
        <draggable 
          v-if="0 < images.length" 
          v-model="images" 
          class="images-preview-container"
          @end="handleDragEnd"
          item-key="id"
          handle=".image-item"
        >
          <template #item="{ element, index }">
            <div class="image-item">
              <img :src="element.preview" alt="Preview" class="image-preview">
              <button type="button" class="remove-button" @click="removeImage(index)">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </template>
        </draggable>
        
        <!-- アップロードエリア -->
        <div 
          class="upload-container"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
          :class="{ 'dragging': isDragging }"
        >
          <div class="upload-placeholder">
            <i class="fas fa-cloud-upload-alt"></i>
            <p>画像をドラッグ＆ドロップまたは<span class="file-select-link" @click="triggerFileInput">ファイルを選択</span></p>
            <p class="file-types">対応ファイル形式: PNG, JPG, JPEG, BMP</p>
            <input 
              type="file" 
              :id="id" 
              :name="id" 
              @change="handleFileSelect" 
              accept=".png,.jpg,.jpeg,.bmp"
              class="file-input"
              ref="fileInput"
              multiple
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'FormImageUpload',
  components: {
    draggable
  },
  props: {
    label: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    modelValue: {
      type: Array,
      default: () => []
    },
    required: {
      type: Boolean,
      default: false
    },
    maxImages: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      isDragging: false,
      images: []
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (Array.isArray(newValue) && newValue.length > 0) {
          this.images = newValue.map(item => {
            if (item instanceof File) {
              return {
                file: item,
                preview: URL.createObjectURL(item)
              }
            } else if (typeof item === 'string') {
              return {
                file: null,
                preview: item
              }
            } else if (item && typeof item === 'object') {
              // 既存画像オブジェクトの場合
              if (item.preview) {
                return {
                  id: item.id,
                  file: null,
                  preview: item.preview,
                  isExisting: item.isExisting || false,
                  productId: item.productId,
                  fileName: item.fileName,
                  sortOrder: item.sortOrder
                }
              }
            }
            return null
          }).filter(item => item !== null)
        } else {
          this.images = []
        }
      }
    }
  },
  emits: ['update:modelValue', 'image-deleted', 'images-reordered', 'image-uploaded'],
  methods: {
    handleDragOver() {
      this.isDragging = true
    },
    handleDragLeave() {
      this.isDragging = false
    },
    handleDrop(event) {
      this.isDragging = false
      const files = Array.from(event.dataTransfer.files)
      this.processFiles(files)
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.processFiles(files)
    },
    handleDragEnd() {
      // ドラッグ終了時にsortOrderを更新
      this.images.forEach((image, index) => {
        if (image.sortOrder !== undefined) {
          image.sortOrder = index;
        }
      });
      
      // 親コンポーネントに画像の並び替えを通知
      this.$emit('images-reordered', this.images);
      this.updateModelValue();
    },

    /**
     * アップロードされたファイルを処理する
     * 1. ファイル形式のバリデーション
     * 2. ファイルサイズのチェック（10MB上限）
     * 3. 同一ファイル名のチェック
     * 4. 最大アップロード数のチェック（10枚まで）
     * 5. 画像のリサイズ処理
     * @param {FileList} files - アップロードされたファイルリスト
     */
    async processFiles(files) {
      try {
        // 有効な画像形式のみをフィルタリング
        const validExtensions = ['.png', '.jpg', '.jpeg', '.bmp']
        const validFiles = files.filter(file => {
          const extension = '.' + file.name.split('.').pop().toLowerCase()
          return validExtensions.includes(extension) && file.type.startsWith('image/')
        })
        
        if (validFiles.length === 0) return
        
        // ファイルサイズの上限チェック（10MB）
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
        const oversizedFiles = validFiles.filter(file => file.size > MAX_FILE_SIZE);
        if (oversizedFiles.length > 0) {
          alert(`ファイルサイズが10MBを超えるファイルがあります。\n${oversizedFiles.map(f => f.name).join('\n')}`);
          return;
        }
        
        // 同一ファイル名のチェック
        // 既存の画像ファイル名を取得
        const existingFilenames = this.images
          .filter(img => img.file || img.fileName)
          .map(img => img.file ? img.file.name : img.fileName);
        
        // 新しいファイル名を取得
        const newFilenames = validFiles.map(file => file.name);
        
        // 新しいファイル内での重複をチェック
        const duplicatesInNewFiles = newFilenames.filter((name, index) => 
          newFilenames.indexOf(name) !== index
        );
        
        // 既存ファイルとの重複をチェック
        const duplicatesWithExisting = newFilenames.filter(name => 
          existingFilenames.includes(name)
        );
        
        // 重複がある場合はエラーメッセージを表示
        if (duplicatesInNewFiles.length > 0 || duplicatesWithExisting.length > 0) {
          const allDuplicates = [...new Set([...duplicatesInNewFiles, ...duplicatesWithExisting])];
          alert(`同一ファイル名のファイルが複数存在します。\n${allDuplicates.join('\n')}`);
          return;
        }
        
        // 最大アップロード数のチェック
        const remainingSlots = this.maxImages - this.images.length
        const filesToAdd = validFiles.slice(0, remainingSlots)
        
        if (filesToAdd.length === 0) {
          if (validFiles.length > remainingSlots) {
            alert(`画像は最大${this.maxImages}枚までアップロードできます。`);
          }
          return;
        }
        
        const newImages = [...this.images]
        
        // 各ファイルをリサイズして処理
        for (const file of filesToAdd) {
          try {
            // 画像をリサイズ（800x600以内、アスペクト比維持）
            const resizedFile = await this.resizeImage(file);
            
            // プレビュー用のURLを作成
            const previewUrl = URL.createObjectURL(resizedFile);
            
            // 画像情報をリストに追加
            newImages.push({
              file: resizedFile,         // リサイズ後のファイル
              preview: previewUrl,       // プレビュー用URL
              fileName: file.name,       // 元のファイル名
              originalFile: file         // 元のファイル（バリデーション用）
            });
          } catch (error) {
            // リサイズに失敗した場合は元のファイルを使用
            const previewUrl = URL.createObjectURL(file);
            newImages.push({
              file: file,
              preview: previewUrl,
              fileName: file.name
            });
          }
        }
        
        // 画像リストを更新
        this.images = newImages;
        this.updateModelValue();
        
        // 画像がアップロードされたことを親コンポーネントに通知
        this.$emit('image-uploaded');
      } catch (error) {
        alert('画像の処理中にエラーが発生しました。');
      }
    },
    
    /**
     * 画像をリサイズする（最大幅800px、最大高さ600px、アスペクト比維持）
     * 
     * @param {File} file - リサイズする画像ファイル
     * @returns {Promise<File>} リサイズされた画像ファイル
     */
    resizeImage(file) {
      return new Promise((resolve, reject) => {
        try {
          // 最大サイズの定義
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 600;
          
          // 画像をロード
          const img = new Image();
          
          img.onload = () => {
            try {
              // 元の画像サイズを取得
              const originalWidth = img.width;
              const originalHeight = img.height;
              
              // リサイズが必要かどうかを確認
              if (originalWidth <= MAX_WIDTH && originalHeight <= MAX_HEIGHT) {
                // リサイズ不要の場合は元のファイルを返す
                URL.revokeObjectURL(img.src); // メモリリーク防止
                resolve(file);
                return;
              }
              
              // 新しいサイズを計算（アスペクト比を維持）
              let newWidth, newHeight;
              
              // 幅と高さの縮小比率を計算
              const widthRatio = MAX_WIDTH / originalWidth;
              const heightRatio = MAX_HEIGHT / originalHeight;
              
              // 小さい方の比率を使用して、両方の制限内に収める
              const ratio = Math.min(widthRatio, heightRatio);
              
              // 新しいサイズを計算
              newWidth = Math.floor(originalWidth * ratio);
              newHeight = Math.floor(originalHeight * ratio);
              
              // キャンバスを作成してリサイズ
              const canvas = document.createElement('canvas');
              canvas.width = newWidth;
              canvas.height = newHeight;
              
              // 画像をキャンバスに描画
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, newWidth, newHeight);
              
              // キャンバスからBlobを作成（品質パラメータ0.9で高品質を維持）
              canvas.toBlob(
                (blob) => {
                  try {
                    if (!blob) {
                      URL.revokeObjectURL(img.src);
                      reject(new Error('画像の変換に失敗しました'));
                      return;
                    }
                    
                    // BlobからFileオブジェクトを作成
                    const resizedFile = new File([blob], file.name, {
                      type: blob.type || file.type, // blobのタイプを優先
                      lastModified: file.lastModified
                    });
                    
                    URL.revokeObjectURL(img.src); // メモリリーク防止
                    resolve(resizedFile);
                  } catch (error) {
                    URL.revokeObjectURL(img.src); // メモリリーク防止
                    reject(error);
                  }
                },
                file.type,
                0.9 // 品質パラメータ (0-1)
              );
            } catch (error) {
              URL.revokeObjectURL(img.src); // メモリリーク防止
              reject(error);
            }
          };
          
          img.onerror = () => {
            URL.revokeObjectURL(img.src); // メモリリーク防止
            reject(new Error('画像の読み込みに失敗しました'));
          };
          
          // 画像のURLを設定
          img.src = URL.createObjectURL(file);
        } catch (error) {
          reject(error);
        }
      });
    },
    removeImage(index) {
      if (index >= 0 && index < this.images.length) {
        // 既存画像の場合、削除リストに追加するイベントを発行
        if (this.images[index].isExisting && this.images[index].id) {
          this.$emit('image-deleted', this.images[index].id);
        } else {
          // 新規画像の場合も削除イベントを発行（isChangedフラグ設定のため）
          this.$emit('image-deleted', null);
        }
        
        // Release object URL to prevent memory leaks
        if (this.images[index].preview && this.images[index].file) {
          URL.revokeObjectURL(this.images[index].preview)
        }
        
        const newImages = [...this.images]
        newImages.splice(index, 1)
        this.images = newImages
        this.updateModelValue()
      }
    },
    updateModelValue() {
      // 画像オブジェクトを送信して、メタデータを保持する
      this.$emit('update:modelValue', this.images)
    },
    triggerFileInput() {
      if (this.images.length < this.maxImages) {
        this.$refs.fileInput.click()
      }
    }
  },
  beforeUnmount() {
    // Clean up object URLs to prevent memory leaks
    this.images.forEach(image => {
      if (image.preview && image.file) {
        URL.revokeObjectURL(image.preview)
      }
    })
  }
}
</script>

<style scoped>
.form-field-container {
  display: flex;
  align-items: center;
  background-color: #FFF;
  padding: 10px 30px;
  margin: 5px;
  border-radius: 4px;
}

.form-label {
  min-width: 180px;
  font-weight: 700;
  flex: 0 0 180px;
}

.required-mark-container {
  width: 40px;
  margin-right: 10px;
  flex-shrink: 0;
}

.required-mark {
  background-color: #ff0000;
  color: white;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
  text-align: center;
  display: inline-block;
}

.upload-wrapper-container {
  flex: 1;
  min-width: 200px;
}

.image-upload-wrapper {
  width: 100%;
}

.images-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
  min-height: 140px;
}

.image-item {
  position: relative;
  width: 180px;
  height: 140px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  background-color: white;
}

.image-item {
  position: relative;
  width: 180px;
  height: 140px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  object-fit: cover;
  min-width: 100%;
  min-height: 100%;
  max-width: 180px;
  max-height: 140px;
  width: auto;
  height: auto;
}

.remove-button {
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
}

.remove-button:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.upload-container {
  width: 100%;
  height: 120px;
  border: 2px dashed #195983;
  border-radius: 4px;
  background-color: #f5f5f5;
  position: relative;
  transition: all 0.3s ease;
}

.upload-container.dragging {
  border-color: #0d4b6f;
  background-color: #e4e4e4;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #195983;
  cursor: pointer;
}

.upload-placeholder i {
  font-size: 36px;
  margin-bottom: 10px;
}

.upload-placeholder p {
  margin: 0;
  font-size: 14px;
  text-align: center;
}

.file-types {
  margin-top: 5px;
  font-size: 12px;
  color: #666;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.file-select-link {
  color: #195983;
  text-decoration: underline;
  cursor: pointer;
}

.drag-handle {
  position: absolute;
  top: 5px;
  left: 5px;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: move;
  color: white;
  transition: all 0.3s ease;
}

.drag-handle:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

</style>
